/**
 * Virtual field: isDebited
 * Determines if a workout should be debited from client balance
 * This mirrors the backend logic in backend/server.js
 * 
 * Logic:
 * - If status === 'skipped' (Пропуск): return true
 * - If status === 'cancelled' (Отменено) or 'transferred' (Перенесено): return false
 * - If current time > end_time: return true
 * - Otherwise: return false
 * 
 * @param {Object} workout - Workout object with date, time, end_time, status
 * @param {Date} currentTime - Current time (optional, defaults to new Date())
 * @returns {boolean} - true if workout should be debited
 */
export const isWorkoutDebited = (workout, currentTime = null) => {
  if (!workout) return false;
  
  const now = currentTime || new Date();
  
  // Exception: Cancelled or Transferred workouts are never debited
  if (workout.status === 'cancelled' || workout.status === 'transferred') {
    return false;
  }
  
  // Status-based: Skipped (Пропуск) workouts are always debited
  if (workout.status === 'skipped') {
    return true;
  }
  
  // Time-based: If workout end time has passed, it should be debited
  if (workout.date && workout.time) {
    try {
      // Parse workout date and time
      const [year, month, day] = workout.date.split('-').map(Number);
      const [hours, minutes] = workout.time.split(':').map(Number);
      
      // Calculate end time (use end_time if available, otherwise add 1 hour)
      let endDateTime;
      const startDateTime = new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
      if (workout.end_time) {
        const [endHours, endMinutes] = workout.end_time.split(':').map(Number);
        endDateTime = new Date(year, month - 1, day, endHours, endMinutes || 0, 0, 0);
        if (endDateTime <= startDateTime) {
          endDateTime.setDate(endDateTime.getDate() + 1);
        }
      } else {
        endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);
      }

      // Check if current time has passed the end time
      return now > endDateTime;
    } catch (error) {
      console.error('[isDebited] Error calculating end time:', error);
      return false;
    }
  }
  
  return false;
};

/**
 * Calculate client balance from workouts and payments
 * Uses virtual isDebited field
 * 
 * @param {Array} workouts - Array of workout objects
 * @param {number} totalPayments - Total payments/credits
 * @param {Date} currentTime - Current time (optional)
 * @returns {number} - Balance (payments - debited workouts)
 */
export const calculateBalance = (workouts = [], totalPayments = 0, currentTime = null) => {
  const debitedCount = workouts.filter(workout => 
    isWorkoutDebited(workout, currentTime)
  ).length;
  
  return totalPayments - debitedCount;
};

/**
 * Get the next workout end time that will trigger a debit
 * Used for scheduling real-time balance updates
 * 
 * @param {Array} workouts - Array of workout objects
 * @param {Date} currentTime - Current time (optional)
 * @returns {Date|null} - Next end time that will trigger debit, or null
 */
export const getNextDebitTime = (workouts = [], currentTime = null) => {
  const now = currentTime || new Date();
  let nextDebitTime = null;
  
  for (const workout of workouts) {
    // Skip workouts that are already debited or should never be debited
    if (isWorkoutDebited(workout, now) || 
        workout.status === 'cancelled' || 
        workout.status === 'transferred') {
      continue;
    }
    
    // Skip skipped workouts (already debited)
    if (workout.status === 'skipped') {
      continue;
    }
    
    // Calculate end time
    if (workout.date && workout.time) {
      try {
        const [year, month, day] = workout.date.split('-').map(Number);
        const [hours, minutes] = workout.time.split(':').map(Number);
        
        let endDateTime;
        if (workout.end_time) {
          const [endHours, endMinutes] = workout.end_time.split(':').map(Number);
          endDateTime = new Date(year, month - 1, day, endHours, endMinutes || 0, 0, 0);
        } else {
          endDateTime = new Date(year, month - 1, day, hours, minutes || 0, 0, 0);
          endDateTime.setHours(endDateTime.getHours() + 1);
        }
        
        // Only consider future end times
        if (endDateTime > now) {
          if (!nextDebitTime || endDateTime < nextDebitTime) {
            nextDebitTime = endDateTime;
          }
        }
      } catch (error) {
        console.error('[getNextDebitTime] Error calculating end time:', error);
      }
    }
  }
  
  return nextDebitTime;
};

